import { Link } from "react-router-dom";
import { CURRENCY_SYMBOLS } from "../lib/currencies";

const FALLBACK =
  "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?w=1200&q=80&auto=format&fit=crop";

const TYPE_COLORS  = { hotel: "#1351A8", cruise: "#0A7A5E", package: "#C25A0A" };
const TYPE_LABELS  = { hotel: "HOTEL",   cruise: "CRUISE",  package: "PACKAGE" };

export default function DealCard({ deal, index = 0 }) {
  const img      = deal.image_url || FALLBACK;
  const slug     = deal.slug || deal.id;
  const dealType = deal.deal_type || "flight";
  const s        = CURRENCY_SYMBOLS[deal.price_currency] || "€";

  // ── type-specific derived values ────────────────────────────────────────────────────────────
  const badgeLabel = TYPE_LABELS[dealType];
  const badgeColor = TYPE_COLORS[dealType];

  const priceLabel = dealType === "hotel"
    ? `${s}${deal.price_per_person || deal.price_eur}/night`
    : dealType === "cruise" || dealType === "package"
    ? `${s}${deal.price_eur} p.p.`
    : `${s}${deal.price_eur} ${{ round_trip: "RT", one_way: "OW", multi_city: "MULTI" }[deal.trip_type] || "RT"}`;

  const eyebrow = dealType === "hotel"
    ? [deal.star_rating && `${deal.star_rating}★`, deal.board_type, deal.checkin_period || deal.travel_window].filter(Boolean).join(" · ")
    : dealType === "cruise"
    ? (() => {
        const route = deal.departure_port && deal.destination_port
          ? `${deal.departure_port} → ${deal.destination_port}`
          : deal.departure_port || deal.destination_port || deal.cruise_route || deal.to_city || "";
        return [route, deal.duration_nights && `${deal.duration_nights} nights`].filter(Boolean).join(" · ");
      })()
    : dealType === "package"
    ? [deal.package_includes, deal.travel_window].filter(Boolean).join(" · ")
    : [deal.airline, deal.travel_window].filter(Boolean).join(" · ");

  const _returnFrom = deal.return_from_city || deal.return_from_airport || "";
  const _returnTo   = deal.return_to_city || deal.return_to_airport || deal.from_city || "";
  const _retKey     = _returnFrom.toUpperCase().split("(")[0].trim();
  const _toKey      = (deal.to_city || deal.to_airport || "").toUpperCase().split("(")[0].trim();
  const isOpenJaw   = Boolean(_returnFrom && _retKey !== _toKey);
  const _isMultiCity = deal.trip_type === "multi_city" || deal.trip_type === "multi";
  const _showTwoLegs = isOpenJaw || (_isMultiCity && Boolean(_returnFrom));

  const title = dealType === "hotel"
    ? (deal.hotel_name || deal.to_city)
    : dealType === "cruise"
    ? (deal.cruise_line || "Cruise")
    : _showTwoLegs
    ? <>{deal.from_city} <span className="text-[#CC1A1A]">→</span> {deal.to_city} <span className="text-[#555]">/</span> {_returnFrom} <span className="text-[#CC1A1A]">→</span> {_returnTo}</>
    : <>{deal.from_city} <span className="text-[#CC1A1A]">→</span> {deal.to_city}</>;

  return (
    <Link
      to={`/deal/${slug}`}
      className="block group deal-card fade-up"
      style={{ animationDelay: `${Math.min(index * 50, 400)}ms`, opacity: deal.expired ? 0.7 : 1 }}
      data-testid={`deal-card-${deal.id}`}
    >
      <div
        className="bg-[#141414] deal-card-img w-full"
        style={{ height: "240px", overflow: "hidden", position: "relative", width: "100%" }}
      >
        <img
          src={img}
          alt={`${deal.from_city || deal.hotel_name || ""} ${deal.to_city || ""}`}
          loading="lazy"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: deal.photo_position || "center",
            display: "block",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 via-[#111111]/10 to-transparent pointer-events-none" />

        {deal.expired && (
          <div
            className="absolute pointer-events-none"
            style={{
              top: 22, right: -32,
              transform: "rotate(45deg)",
              background: "white", color: "#CC1A1A",
              fontFamily: "monospace", fontSize: 13, fontWeight: 700,
              letterSpacing: "0.18em", padding: "6px 40px",
              border: "1px solid #CC1A1A", zIndex: 10,
            }}
          >
            EXPIRED
          </div>
        )}

        <div className="absolute top-0 left-0 right-0 flex items-center justify-end gap-2 p-3">
          {dealType === "flight" && deal.cabin_class === "business" && (
            <span className="rd-badge" style={{ background: "#C8922A" }}>BUSINESS</span>
          )}
          {dealType === "flight" && deal.cabin_class === "first" && (
            <span className="rd-badge" style={{ background: "#6C3FC5" }}>FIRST</span>
          )}
          {badgeLabel
            ? <span className="rd-badge" style={{ background: badgeColor }}>{badgeLabel}</span>
            : <span className="rd-badge">{deal.tags?.[0] || "CLASSIFIED"}</span>
          }
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center gap-2">
          <span className="rd-badge rd-badge-yellow">{priceLabel}</span>
        </div>
      </div>

      <div className="pt-5">
        <p className="eyebrow">{eyebrow}</p>
        <h3 className="font-mono text-xl leading-tight mt-2 text-white group-hover:text-[#CC1A1A] transition-colors tracking-tight">
          {title}
        </h3>
        {deal.teaser && (
          <p className="mt-2 text-sm text-[#9A8F83] leading-relaxed line-clamp-2 font-sans">{deal.teaser}</p>
        )}
      </div>
    </Link>
  );
}
